import { useState, useEffect, useRef } from 'react';
import useThreadData from './useThreadData';
import { getDocRef, getPrevSessionMessages, getIdentifiers, setPrevSessionMessages, setPrevSessionThreadId, getNewDocRef } from './firebase';
import { Timestamp, setDoc, arrayUnion } from 'firebase/firestore';
import { MSG_TYPE_ENUM } from './Message';
import useLocation from './useLocation';
import { uploadFile } from './firebase';

/*
    LOADING: 0,
    TEXT_VISITOR: 1,
    TEXT_CLIENT: -1,
    FILE_VISITOR: 2,
    FILE_CLIENT: -2,
    AUTO_REPLY_VISITOR: 3,
    AUTO_REPLY_CLIENT: -3,
    AUTO_SOLICIT: -4,
    AUTO_SOLICIT_SAVED: -5,
    AUTO_SOLICIT_INFO: -6,
    AUTO_ENGAGE: -7,
    AUTO_SOLICIT_CLOSED: -8,
    WELCOME_MSG: -9,
    EMAIL_VISITOR: 4,
    ERROR: -10,
    DISABLED: -11,
*/

const { 
    TEXT_VISITOR,
    TEXT_CLIENT,
    FILE_VISITOR,
    FILE_CLIENT,
    AUTO_ENGAGE,
    AUTO_REPLY_CLIENT,
    AUTO_REPLY_VISITOR,
    AUTO_SOLICIT,
    AUTO_SOLICIT_SAVED,
    AUTO_SOLICIT_INFO,
    AUTO_SOLICIT_CLOSED,
    WELCOME_MSG,
    EMAIL_VISITOR,
    ERROR,
    LOADING,
    DISABLED,
} = MSG_TYPE_ENUM;

export default function useMessages({ config, open, currentUser }) {
    const [messages, setMessages] = useState([]);
    const [localMessages, setLocalMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(true);
    const [msgError, setMsgError] = useState(null);
    const [threadData, threadLoading, threadError, docRef] = useThreadData({ uid: currentUser?.uid });
    const [location, locationLoading, locationError] = useLocation(open);
    const [generalLoading, setGeneralLoading] = useState(false);
    const latestSeenThreadIndex = useRef(null);

    const defaultConfig = config?.default;
    // useCallback to stop unneccessary re-rendering of all these helper functions

    async function addMessage(message) {
        switch (message.msgType) {
            case TEXT_VISITOR:
            case FILE_VISITOR:
                await sendMessage(message);
                break;
            case AUTO_ENGAGE:
                setLocalMessages((prevLocalMessages) => {
                    if (prevLocalMessages.length !== 0) {
                        return prevLocalMessages;
                    }
                    if (threadData && threadData.messages.length !== 0) {
                        return prevLocalMessages;
                    }
                    if (threadData && threadData.banned) {
                        return prevLocalMessages;
                    }
                    if (messages.length !== 0) {
                        return prevLocalMessages;
                    }
                    const newLocalMessages = [...prevLocalMessages, message];
                    setPrevSessionMessages(newLocalMessages);
                    return newLocalMessages;
                });
                break;
            case AUTO_REPLY_VISITOR:
                setLocalMsg(message);
                break;
            case AUTO_REPLY_CLIENT:
                setLocalMsg(message);
                break;
            case AUTO_SOLICIT:
                setLocalMsg(message);
                break;
            case WELCOME_MSG:
                setLocalMsg(message);
                break;
            case EMAIL_VISITOR://EMAIL_VISITOR
                sendEmailMessage(message);
                break;
            case ERROR:
                setLocalMessages((prevLocalMessages) => {
                    // Filter out any previous error messages
                    const filteredMessages = prevLocalMessages.filter(msg => msg.msgType !== ERROR);
                    // Add the new error message
                    const newLocalMessages = [...filteredMessages, message];
                    setPrevSessionMessages(newLocalMessages);
                    return newLocalMessages;
                });
                break;
            default:
                throw new Error('Invalid message type');
        }
    }

    function setLocalMsg(message) {
        if (!defaultConfig) {
            return;
        }
        setLocalMessages((prevLocalMessages) => {
            const newLocalMessages = [...prevLocalMessages, message];
            setPrevSessionMessages(newLocalMessages);
            return newLocalMessages;
        });
    }

    function updateSeen(latestIndex, msgType) {
        if (msgType === TEXT_CLIENT || msgType === FILE_CLIENT) {
            latestSeenThreadIndex.current = latestIndex;
            messages[latestIndex].seen = true;
            setPrevSessionMessages(localMessages, latestIndex);
        } else {
            setLocalMessages((prevLocalMessages) => {
                const latestMessage = prevLocalMessages[prevLocalMessages.length - 1];
                latestMessage.seen = true;
                setPrevSessionMessages(prevLocalMessages);
                return prevLocalMessages;
            });
        }
    }
    
    async function sendEmailMessage(message) {
        if (!defaultConfig) {
            return;
        }
        try {
            console.log("Sending email message...");
            if (!location) {
                throw new Error('Invalid location');
            }
            let newThread = docRef.current ? false : true;
            let emailMsgRef = newThread ? getNewDocRef() : docRef.current;
            let docTimestamp = Timestamp.now();
            let banStatus = false;
            let url = window.location.href;
            let distinguisherId = config?.distinguisherSet ? config.distinguisherSet : "00/" + Math.floor(Math.random() * 25).toString().padStart(2, '0');
            if (threadData) {
                docTimestamp = threadData.createdAt;
                banStatus = threadData.banned;
                url = threadData.url ? threadData.url : url;
                distinguisherId = threadData.distinguisherId ? threadData.distinguisherId : distinguisherId;
            }
            const payload = {
                emailNewThread: newThread,
                sendYGM: message.sendYGM ? message.sendYGM : false,
                createdAt: docTimestamp,
                banned: banStatus,
                location,
                name: message.name,
                email: message.email,
                url,
                distinguisherId,
                createContact: config?.contactSetting,
                ...getIdentifiers(),
                messages: arrayUnion(message)
            };
            await setDoc(emailMsgRef, payload, { merge: true });
        } catch (e) {
            console.error(e);
        }
    }

    async function onSolicitSaved(params) {
        if (!defaultConfig) {
            return;
        }
        if (!params) {
            setLocalMessages((prevLocalMessages) => {
                const newLocalMessages = prevLocalMessages.map((message) => {
                    if (message.msgType === AUTO_SOLICIT) {
                        return {
                            ...message,
                            msgType: AUTO_SOLICIT_CLOSED,
                            text: "",
                        };
                    }
                    return message;
                });
                setPrevSessionMessages(newLocalMessages);
                return newLocalMessages;
            });
            return;
        }
        const { name, email } = params;
        try {
            console.log("Handling solicit saved...")
            if (!location) {
                throw new Error('Invalid location');
            }
            let newThread = false;
            if (!docRef.current) {
                docRef.current = getDocRef(currentUser?.uid);
                setPrevSessionThreadId(docRef.current.id);
                newThread = true;
            }
            let docTimestamp = Timestamp.now();
            let banStatus = false;
            let url = window.location.href;
            let distinguisherId = config?.distinguisherSet ? config.distinguisherSet : "00/" + Math.floor(Math.random() * 25).toString().padStart(2, '0');
            if (threadData) {
                docTimestamp = threadData.createdAt;
                banStatus = threadData.banned;
                url = threadData.url ? threadData.url : url;
                distinguisherId = threadData.distinguisherId ? threadData.distinguisherId : distinguisherId;
            }
            setLocalMessages((prevLocalMessages) => {
                const newLocalMessages = prevLocalMessages.map((message) => {
                    if (message.msgType === AUTO_SOLICIT) {
                        return {
                            ...message,
                            msgType: AUTO_SOLICIT_SAVED,
                            text: "Thank you, we will get back to you shortly.",
                        };
                    }
                    return message;
                });
                setPrevSessionMessages(newLocalMessages);
                return newLocalMessages;
            });  
            console.log("Solicit saved: ", name, email);
            newThread ?
            await setDoc(docRef.current, {
                createdAt: docTimestamp,
                banned: banStatus,
                location,
                name,
                email,
                url,
                distinguisherId,
                createContact: config?.contactSetting,
                ...getIdentifiers(),
            }, { merge: true })
            : // kinda scuffed but it works
            await setDoc(docRef.current, {
                createdAt: docTimestamp,
                banned: banStatus,
                location,
                name,
                email,
                url,
                distinguisherId,
                createContact: config?.contactSetting,
                ...getIdentifiers(),
                messages: arrayUnion({
                    msgType: AUTO_SOLICIT_INFO,
                    createdAt: Timestamp.now(),
                })
            }, { merge: true });
        } catch (e) {
            console.error(e);
        }
    }

    async function sendMessage(message) {
        if (!defaultConfig) {
            return;
        }
        try {
            console.log("Handling sendMessage...")
            if (!location) {
                throw new Error('Invalid location');
            }
            console.log(JSON.stringify(getIdentifiers()));
            if (!docRef.current) {
                docRef.current = getDocRef(currentUser?.uid);
                setPrevSessionThreadId(docRef.current.id);
            }
            let docTimestamp = Timestamp.now();
            let banStatus = false;
            let url = window.location.href;
            let distinguisherId = config?.distinguisherSet ? config.distinguisherSet : "00/" + Math.floor(Math.random() * 25).toString().padStart(2, '0');
            if (threadData) {
                docTimestamp = threadData.createdAt;
                banStatus = threadData.banned ? threadData.banned : false;
                url = threadData.url ? threadData.url : url;
                distinguisherId = threadData.distinguisherId ? threadData.distinguisherId : distinguisherId;
            }
            setGeneralLoading(false);
            setMessages((prevMessages) => [...prevMessages, message]);
            await setDoc(docRef.current, {
                createdAt: docTimestamp,
                banned: banStatus,
                location,
                url,
                name: currentUser?.uid,
                email: currentUser?.email,
                createContact: config?.contactSetting,
                distinguisherId,
                ...getIdentifiers(),
                messages: arrayUnion(message)
            }, { merge: true });
        } catch (e) {
            console.error(e);
        }
    }

    async function handleFileUpload(plainFiles, filesContent) {
        if (!defaultConfig) {
            return;
        }
        setGeneralLoading(true);
        var resultArray = [];
        for (let i = 0; i < plainFiles.length; i++) {
            console.log("Uploading file: " + plainFiles[i].name);
            const resultURL = await uploadFile(plainFiles[i]);
            console.log("File uploaded");
            resultArray.push({
                resultURL,
                fileName: plainFiles[i].name,
                type: plainFiles[i].type,
            });
        }
        console.log(JSON.stringify(resultArray));
        await addMessage({
            text: resultArray.length > 1 ? "Files Uploaded: " + resultArray.length : resultArray[0].fileName,
            msgType: MSG_TYPE_ENUM.FILE_VISITOR,
            createdAt: Timestamp.now(),
            seen: false,
            fileURLArray: resultArray,
        });  
    }

    function merge() {
        if (!defaultConfig) {
            return;
        }
        const merged = [];
        const local = localMessages ? localMessages : [];
        const remote = threadData ? threadData.messages : [];
        let i = 0;
        let j = 0;
        while (i < local.length && j < remote.length) {
            const compareBool = 
                local[i].createdAt.seconds === remote[j].createdAt.seconds 
                ? 
                    local[i].createdAt.nanoseconds < remote[j].createdAt.nanoseconds 
                : 
                    local[i].createdAt.seconds < remote[j].createdAt.seconds;
            if (compareBool) {
                merged.push(local[i]);
                i++;
            } else {
                merged.push(remote[j]);
                j++;
            }
        }
        while (i < local.length) {
            merged.push(local[i]);
            i++;
        }
        while (j < remote.length) {
            merged.push(remote[j]);
            j++;
        }
        if (latestSeenThreadIndex.current && latestSeenThreadIndex.current < merged.length && merged[latestSeenThreadIndex.current]) {
            merged[latestSeenThreadIndex.current].seen = true;
        }
        return merged;
    }

    async function sendEngage(autoEngage, open) {
        if (!defaultConfig) {
            return;
        }
        console.log("begin auto engage timeout...");
        if (!config?.autoEngageDelay) {
            return;
        }
        console.log("auto engage delay: ", config.autoEngageDelay);
        await new Promise(resolve => setTimeout(resolve, config.autoEngageDelay * 1000));
        if (messages.length !== 0 || open) {
            return;
        }
        console.log("Sending auto engage message...");
        addMessage({
            text: autoEngage.message,
            msgType: AUTO_ENGAGE,
            createdAt: Timestamp.now(),
            seen: open,
        });
    }

    async function handleAutoEngage(autoEngages, open){
        if (!defaultConfig) {
            return;
        }
        console.log("Handling auto engage...");
        if (!autoEngages || autoEngages.length === 0) {
            console.log("No auto engages available");
            return;
        }
        if (messages.length !== 0 || messagesLoading) {
            console.log("Messages not empty or loading.");
            return;
        }
        
        // Find the best matching auto engage for current pathname
        const currentPath = window.location.pathname;
        let bestMatch = null;
        let bestMatchScore = -1;
        
        for (const engage of autoEngages) {
            // If no path specified, it's a global engage
            if (!engage.path) {
                if (bestMatchScore < 0) {
                    bestMatch = engage;
                    bestMatchScore = 0;
                }
                continue;
            }
            
            // Check if current path includes this engage's path
            if (currentPath.includes(engage.path)) {
                // Longer path matches are more specific, so they get higher score
                const matchScore = engage.path.length;
                if (matchScore > bestMatchScore) {
                    bestMatch = engage;
                    bestMatchScore = matchScore;
                }
            }
        }
        
        if (bestMatch) {
            console.log("Found best matching auto engage:", bestMatch);
            sendEngage(bestMatch, open);
        } else {
            console.log("No matching auto engage found for path:", currentPath);
        }
    };

    useEffect(() => {
        if (!defaultConfig) {
            return;
        }
        const payload = getPrevSessionMessages();
        if (payload) {
            latestSeenThreadIndex.current = payload.latestSeenThreadIndex;
            setLocalMessages(payload.messages);
        }
    }, [defaultConfig]);

    useEffect(() => {
        if (!defaultConfig) {
            return;
        }
        if (threadLoading) {
            return;
        }
        if (threadError) {
            setMsgError(threadError);
            return;
        }
        const merged = merge();
        setMessages(merged);
        if (messagesLoading) {
            setMessagesLoading(false);
        }
    }, [threadData?.messages, threadLoading, threadError, localMessages, defaultConfig]);

    useEffect(() => {
        if (!defaultConfig) {
            return;
        }
        if (generalLoading) {
            setMessages((prevMessages) => [...prevMessages, {
                text: "",
                msgType: LOADING,
                createdAt: Timestamp.now(),
                seen: false,
            }]);
        } else {
            setMessages((prevMessages) => prevMessages.filter(msg => msg.msgType !== LOADING));
        }
    }, [generalLoading]);
    
    const handlers = {
        onSolicitSaved: defaultConfig ? onSolicitSaved : () => {},
        handleAutoEngage: defaultConfig ? handleAutoEngage : () => {},
        addMessage: defaultConfig ? addMessage : () => {},
        handleFileUpload: defaultConfig ? handleFileUpload : () => {},
        updateSeen,
        setMessages,
    }

    return [messages, threadData, messagesLoading, msgError || locationError, threadData?.banned || threadData?.muted, handlers];
};
